/**
 * 全局音频管理器
 * 不使用 audio 标签，直接使用 Audio 对象
 * 确保同时只有一个音频在播放
 */
class AudioManager {
    constructor() {
        this.audio = new Audio()
        this.currentSrc = null
        this.currentComponent = null
        this.playbackRate = 1
        this.listeners = new Set()
        this.isPlaying = false

        // 音频事件监听
        this.audio.addEventListener('timeupdate', this.handleTimeUpdate.bind(this))
        this.audio.addEventListener('loadedmetadata', this.handleLoadedMetadata.bind(this))
        this.audio.addEventListener('ended', this.handleEnded.bind(this))
    }

    /**
     * 播放指定音频
     * @param {Object} component - Vue 组件实例
     * @param {string} src - 音频 URL
     * @param {number} startTime - 开始播放时间（可选）
     */
    play(component, src, startTime = null) {
        return new Promise((resolve, reject) => {
            // 如果播放的是不同音频，先停止当前播放
            if (this.currentSrc !== src || this.currentComponent !== component) {
                this.stop()
            }

            this.currentSrc = src
            this.currentComponent = component
            this.audio.src = src
            this.audio.playbackRate = this.playbackRate

            if (startTime !== null) {
                this.audio.currentTime = startTime
            }

            this.audio.play()
                .then(() => {
                    this.isPlaying = true
                    this.notifyListeners('play', { component, src })
                    resolve()
                })
                .catch((err) => {
                    console.error('播放失败:', err)
                    this.isPlaying = false
                    this.notifyListeners('error', { component, src, error: err })
                    reject(err)
                })
        })
    }

    /**
     * 暂停音频
     */
    pause() {
        if (this.isPlaying) {
            this.audio.pause()
            this.isPlaying = false
            this.notifyListeners('pause', {
                component: this.currentComponent,
                src: this.currentSrc
            })
        }
    }

    /**
     * 停止音频
     */
    stop() {
        if (this.isPlaying || this.currentSrc) {
            this.audio.pause()
            this.audio.currentTime = 0
            this.isPlaying = false
            this.notifyListeners('stop', {
                component: this.currentComponent,
                src: this.currentSrc
            })
        }
    }

    /**
     * 设置播放进度
     * @param {number} time - 时间（秒）
     */
    seekTo(time) {
        if (this.currentSrc) {
            this.audio.currentTime = time
        }
    }

    /**
     * 设置播放速度
     * @param {number} speed - 播放速度
     */
    setPlaybackRate(speed) {
        this.playbackRate = speed
        if (this.currentSrc) {
            this.audio.playbackRate = speed
        }
    }

    /**
     * 获取当前播放速度
     */
    getPlaybackRate() {
        return this.playbackRate
    }

    /**
     * 获取当前播放时间
     */
    getCurrentTime() {
        return this.audio.currentTime
    }

    /**
     * 获取音频总时长
     */
    getDuration() {
        return this.audio.duration
    }

    /**
     * 获取当前播放状态
     */
    isPlayingAudio() {
        return this.isPlaying
    }

    /**
     * 获取当前音频信息
     */
    getCurrentAudio() {
        return {
            src: this.currentSrc,
            component: this.currentComponent,
            currentTime: this.audio.currentTime,
            duration: this.audio.duration,
            isPlaying: this.isPlaying
        }
    }

    /**
     * 处理时间更新事件
     */
    handleTimeUpdate() {
        this.notifyListeners('timeupdate', {
            currentTime: this.audio.currentTime,
            duration: this.audio.duration,
            component: this.currentComponent
        })
    }

    /**
     * 处理元数据加载完成事件
     */
    handleLoadedMetadata() {
        this.notifyListeners('loadedmetadata', {
            duration: this.audio.duration,
            component: this.currentComponent
        })
    }

    /**
     * 处理播放结束事件
     */
    handleEnded() {
        this.isPlaying = false
        this.notifyListeners('ended', {
            component: this.currentComponent,
            src: this.currentSrc
        })
    }

    /**
     * 添加事件监听器
     * @param {Function} callback - 回调函数
     */
    addListener(callback) {
        this.listeners.add(callback)
    }

    /**
     * 移除事件监听器
     * @param {Function} callback - 回调函数
     */
    removeListener(callback) {
        this.listeners.delete(callback)
    }

    /**
     * 通知所有监听器
     * @param {string} event - 事件类型
     * @param {Object} data - 事件数据
     */
    notifyListeners(event, data) {
        this.listeners.forEach(callback => {
            try {
                callback(event, data)
            } catch (err) {
                console.error('监听器执行失败:', err)
            }
        })
    }

    /**
     * 清理当前音频
     * @param {Object} component - Vue 组件实例
     */
    cleanup(component) {
        if (this.currentComponent === component) {
            this.stop()
            this.currentSrc = null
            this.currentComponent = null
        }
    }

    /**
     * 销毁音频管理器
     */
    destroy() {
        this.stop()
        this.listeners.clear()
    }
}

// 导出单例
const audioManager = new AudioManager()

export default audioManager
